import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getDeviceId } from '../utils/deviceId';

const AuthContext = createContext(null);

import { trackEvent } from '../utils/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios to always send cookies
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // In-memory token for backward compat (not persisted to localStorage)
  const [token, setToken] = useState(null);

  // Set Authorization header when token is available
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check auth status on mount via httpOnly cookie
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch {
        // No valid session — user not authenticated
        setUser(null);
        setToken(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password, device_id: getDeviceId() });
    const data = response.data;
    const { access_token, user: userData } = data;

    // Keep token in memory for bearer auth fallback
    setToken(access_token);
    setUser(userData);
    trackEvent('login', { method: 'email' });
    return userData;
  };

  const register = async (email, password, name, preferredLanguage = 'en') => {
    const response = await axios.post(`${API}/auth/register`, {
      email, password, name,
      preferred_language: preferredLanguage
    });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    setUser(userData);
    trackEvent('sign_up', { method: 'email' });
    return userData;
  };

  const loginWithGoogle = async (sessionId) => {
    const activationCode = sessionStorage.getItem('egns_activation_code');
    if (activationCode) {
      sessionStorage.removeItem('egns_activation_code');
    }

    const response = await axios.post(`${API}/auth/session`, {
      session_id: sessionId,
      activation_code: activationCode || undefined,
      device_id: getDeviceId()
    }, { timeout: 30000 });

    if (response.data.access_token) {
      setToken(response.data.access_token);
    }

    const userData = {
      user_id: response.data.user_id,
      email: response.data.email,
      name: response.data.name,
      picture: response.data.picture,
      preferred_language: response.data.preferred_language,
      current_level: response.data.current_level,
      total_points: response.data.total_points,
      streak_days: response.data.streak_days
    };

    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.warn('Logout request failed (continuing with local cleanup):', error);
    }
    // Clear in-memory state only — cookie cleared server-side
    setToken(null);
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated: !!user,
      login,
      register,
      loginWithGoogle,
      logout,
      updateUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
